import { createFileRoute, Outlet, redirect, useLocation } from '@tanstack/react-router'
import { useEffect } from 'react'
import { getStoredLanguage, setStoredLanguage } from '@/modules/i18n/languageStorage'
import { isPrerenderedPath } from '@/modules/seo/prerenderedPaths'
import type { SupportedLanguage } from '@/modules/i18n/supportedLanguages'
import { NotFoundPage } from '@/components/infoPages/NotFoundPage'

const PREFIXED_LANGS: SupportedLanguage[] = ['he', 'de']

export const Route = createFileRoute('/$lang')({
  beforeLoad: ({ params, location }) => {
    const lang = params.lang as string

    // Strict: only the canonical (no-prefix) path is valid for English.
    // Niche scenario (no internal link emits /en/...), but redirect rather
    // than 404 so the URL "intent" is honored.
    if (lang === 'en') {
      const rest = location.pathname.replace(/^\/en/, '') || '/'
      throw redirect({ to: rest })
    }
  },
  component: LangLayout,
})

function LangLayout() {
  const { lang } = Route.useParams()
  const { pathname } = useLocation()

  // Throwing notFound() from beforeLoad replaces the root component (and the
  // Layout/sidebar with it). Rendering NotFoundPage inline keeps it inside the
  // root Outlet so the Layout still wraps it.
  const isInvalidLang = !PREFIXED_LANGS.includes(lang as SupportedLanguage)
  const isInvalidPath = !isInvalidLang && !isPrerenderedPath(pathname)
  const showNotFound = isInvalidLang || isInvalidPath

  useEffect(() => {
    if (showNotFound) return
    // Seed-on-first-prefixed-visit: if LS is empty, copy URL lang to LS so
    // subsequent navigation to non-prerendered routes stays in this language.
    // Per-spec rule 4: do NOT overwrite a non-empty LS — that's preview-only.
    if (!getStoredLanguage()) {
      setStoredLanguage(lang as SupportedLanguage)
    }
  }, [lang, showNotFound])

  if (showNotFound) return <NotFoundPage />

  return <Outlet />
}
